"use client";

import LocaleLink from "@/component/layout/LocaleLink";
import { useArticleCategories } from "@/hooks/useArticles";
import type { Article, Locale } from "@/types/insights";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";

/** A single article card. */
export default function ArticleCard({ article, i }: { article: Article; i: number }) {
  const locale = useLocale() as Locale;
  const { data: cats = [] } = useArticleCategories();
  const cat = cats.find((c) => c.id === article.category);

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
      className="group"
    >
      <LocaleLink href={`/insights/${article.id}`} className="block">
        <div className="relative aspect-[16/10] overflow-hidden">
          <img src={article.image} alt={article.title[locale]} className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
        </div>
        <div className="mt-4 flex items-center gap-3 text-[11px] uppercase tracking-widest">
          <span className="text-accent">{cat?.label[locale]}</span>
          <span className="text-ash/50 dark:text-white/50">{article.date[locale]}</span>
        </div>
        <h3 className="mt-2 text-lg font-semibold leading-snug text-ash transition-colors group-hover:text-accent dark:text-white">
          {article.title[locale]}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ash/60 dark:text-white/60">{article.excerpt[locale]}</p>
        <span className="mt-3 inline-block text-[11px] uppercase tracking-widest text-ash/40 dark:text-white/40">{article.readingTime[locale]}</span>
      </LocaleLink>
    </motion.article>
  );
}
